"use client"

import * as React from 'react'
import Link from 'next/link'
import type { InjectionWidgetComponentProps } from '@open-mercato/shared/modules/widgets/injection'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { Badge } from '@open-mercato/ui/primitives/badge'
import { Button } from '@open-mercato/ui/primitives/button'
import {
  formatParticipantTypeLabel,
  formatSupportedModeLabel,
  formatSupportedSegmentLabel,
} from '../../../lib/participantProfile'

type ParticipantDetail = {
  id: string
  participantType: string | null
  supportedModes: string[]
  supportedSegments: string[]
  nip: string | null
  regon: string | null
  krs: string | null
  vatEu: string | null
  eori: string | null
  isVerified: boolean
  fleetTrucks: number
  fleetTrailers: number
  fleetLocomotives: number
  fleetWagons: number
  paymentTermsDays: number
  creditLimit: number | null
  creditLimitCurrency: string | null
  memberSince: string | null
  marketplaceGrade: string | null
  marketplaceScore: number | null
  totalShipmentsCompleted: number
  totalVolumeTons: number
}

type ComplianceDocItem = {
  id: string
  documentType: string
  documentNumber: string
  status: string
  verificationStatus: string
  validFrom: string | null
  validUntil: string | null
}

function normalizeText(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
}

function safeNumber(value: unknown, fallback: number): number {
  if (typeof value === 'number' && !Number.isNaN(value)) return value
  return fallback
}

function readCompanyId(context: unknown, data: unknown): string | null {
  const ctx = context && typeof context === 'object' ? context as Record<string, unknown> : {}
  const payload = data && typeof data === 'object' ? data as Record<string, unknown> : {}
  const company = payload.company && typeof payload.company === 'object'
    ? payload.company as Record<string, unknown>
    : {}
  return normalizeText(ctx.companyId)
    ?? normalizeText(ctx.resourceId)
    ?? normalizeText(company.id)
}

function mapParticipant(item: Record<string, unknown>): ParticipantDetail | null {
  const id = normalizeText(item.id)
  if (!id) return null
  return {
    id,
    participantType: normalizeText(item.participantType),
    supportedModes: normalizeStringArray(item.supportedModes),
    supportedSegments: normalizeStringArray(item.supportedSegments),
    nip: normalizeText(item.nip),
    regon: normalizeText(item.regon),
    krs: normalizeText(item.krs),
    vatEu: normalizeText(item.vatEu),
    eori: normalizeText(item.eori),
    isVerified: item.isVerified === true,
    fleetTrucks: safeNumber(item.fleetTrucks, 0),
    fleetTrailers: safeNumber(item.fleetTrailers, 0),
    fleetLocomotives: safeNumber(item.fleetLocomotives, 0),
    fleetWagons: safeNumber(item.fleetWagons, 0),
    paymentTermsDays: safeNumber(item.paymentTermsDays, 30),
    creditLimit: typeof item.creditLimit === 'number' ? item.creditLimit : null,
    creditLimitCurrency: normalizeText(item.creditLimitCurrency),
    memberSince: normalizeText(item.memberSince),
    marketplaceGrade: normalizeText(item.marketplaceGrade),
    marketplaceScore: typeof item.marketplaceScore === 'number' ? item.marketplaceScore : null,
    totalShipmentsCompleted: safeNumber(item.totalShipmentsCompleted, 0),
    totalVolumeTons: safeNumber(item.totalVolumeTons, 0),
  }
}

function mapDocItem(raw: Record<string, unknown>): ComplianceDocItem {
  return {
    id: typeof raw.id === 'string' ? raw.id : '',
    documentType: typeof raw.document_type === 'string' ? raw.document_type : '',
    documentNumber: typeof raw.document_number === 'string' ? raw.document_number : '',
    status: typeof raw.status === 'string' ? raw.status : '',
    verificationStatus: typeof raw.verification_status === 'string' ? raw.verification_status : '',
    validFrom: typeof raw.valid_from === 'string' ? raw.valid_from : null,
    validUntil: typeof raw.valid_until === 'string' ? raw.valid_until : null,
  }
}

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'VALID': return 'default'
    case 'EXPIRING_SOON': return 'secondary'
    case 'EXPIRED': return 'destructive'
    case 'REJECTED': return 'destructive'
    default: return 'outline'
  }
}

function InfoField({ label, value }: { label: string; value: string | number | null }) {
  return (
    <div>
      <div className="text-xs font-medium text-foreground">{label}</div>
      <div className="text-xs text-muted-foreground">{value ?? '—'}</div>
    </div>
  )
}

export default function FreightProfileTabWidget({ context, data }: InjectionWidgetComponentProps) {
  const t = useT()
  const companyId = React.useMemo(() => readCompanyId(context, data), [context, data])
  const [participant, setParticipant] = React.useState<ParticipantDetail | null>(null)
  const [docs, setDocs] = React.useState<ComplianceDocItem[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    let cancelled = false

    async function load() {
      if (!companyId) {
        if (!cancelled) { setLoading(false); setParticipant(null); setDocs([]) }
        return
      }

      setLoading(true)
      setError(null)

      try {
        const participantResponse = await readApiResultOrThrow<{ items?: Array<Record<string, unknown>> }>(
          `/api/participants?customerEntityId=${encodeURIComponent(companyId)}&page=1&pageSize=1`,
          undefined,
          { errorMessage: t('participants.tab.loadError', 'Failed to load freight profile.') },
        )
        if (cancelled) return

        const profile = Array.isArray(participantResponse.items) && participantResponse.items.length > 0
          ? mapParticipant(participantResponse.items[0] as Record<string, unknown>)
          : null

        if (!profile) {
          setParticipant(null)
          setDocs([])
          setLoading(false)
          return
        }

        setParticipant(profile)

        const docsResponse = await readApiResultOrThrow<{ items?: Array<Record<string, unknown>> }>(
          `/api/compliance-documents/documents?participantProfileId=${encodeURIComponent(profile.id)}&page=1&pageSize=20&sortBy=validUntil&sortOrder=asc`,
          undefined,
          { errorMessage: t('participants.tab.docsLoadError', 'Failed to load compliance documents.') },
        )
        if (cancelled) return

        const docItems = Array.isArray(docsResponse.items)
          ? docsResponse.items.map((raw) => mapDocItem(raw as Record<string, unknown>))
          : []

        setDocs(docItems)
      } catch (loadError) {
        if (cancelled) return
        const message = loadError instanceof Error && loadError.message
          ? loadError.message
          : t('participants.tab.loadError', 'Failed to load freight profile.')
        setError(message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => { cancelled = true }
  }, [companyId, t])

  if (!companyId) return null

  if (loading) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        {t('participants.tab.loading', 'Loading freight profile…')}
      </div>
    )
  }

  if (error) {
    return <div className="p-4 text-sm text-destructive">{error}</div>
  }

  if (!participant) {
    return (
      <div className="p-6 text-center space-y-3">
        <div className="text-sm text-muted-foreground">
          {t('participants.tab.noProfile', 'No freight profile linked to this company.')}
        </div>
        <Button asChild size="sm">
          <Link href={`/backend/participants/create?customerEntityId=${encodeURIComponent(companyId)}`}>
            {t('participants.tab.actions.create', 'Create freight profile')}
          </Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {participant.participantType ? (
            <Badge variant="secondary">{formatParticipantTypeLabel(participant.participantType)}</Badge>
          ) : null}
          <Badge variant={participant.isVerified ? 'default' : 'outline'}>
            {participant.isVerified
              ? t('participants.tab.verified', 'Verified')
              : t('participants.tab.unverified', 'Pending verification')}
          </Badge>
        </div>
        <Button asChild size="sm" variant="outline">
          <Link href={`/backend/participants/${participant.id}`}>
            {t('participants.tab.actions.openProfile', 'Open full profile')}
          </Link>
        </Button>
      </div>

      {/* Modes & Segments */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <div className="text-xs font-medium">{t('participants.tab.modes', 'Modes')}</div>
          <div className="flex flex-wrap gap-1">
            {participant.supportedModes.length > 0
              ? participant.supportedModes.map((mode) => (
                  <Badge key={mode} variant="outline">{formatSupportedModeLabel(mode)}</Badge>
                ))
              : <span className="text-xs text-muted-foreground">—</span>}
          </div>
        </div>
        <div className="space-y-1">
          <div className="text-xs font-medium">{t('participants.tab.segments', 'Segments')}</div>
          <div className="flex flex-wrap gap-1">
            {participant.supportedSegments.length > 0
              ? participant.supportedSegments.map((seg) => (
                  <Badge key={seg} variant="outline">{formatSupportedSegmentLabel(seg)}</Badge>
                ))
              : <span className="text-xs text-muted-foreground">—</span>}
          </div>
        </div>
      </div>

      {/* Identifiers */}
      <div>
        <div className="text-xs font-medium mb-2">{t('participants.tab.identifiers', 'Business identifiers')}</div>
        <div className="grid gap-2 text-xs sm:grid-cols-3 md:grid-cols-5">
          <InfoField label="NIP" value={participant.nip} />
          <InfoField label="REGON" value={participant.regon} />
          <InfoField label="KRS" value={participant.krs} />
          <InfoField label="VAT-EU" value={participant.vatEu} />
          <InfoField label="EORI" value={participant.eori} />
        </div>
      </div>

      {/* Fleet & Operations */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <div className="text-xs font-medium mb-2">{t('participants.tab.fleet', 'Fleet summary')}</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <InfoField label={t('participants.form.fleetTrucks', 'Trucks')} value={participant.fleetTrucks} />
            <InfoField label={t('participants.form.fleetTrailers', 'Trailers')} value={participant.fleetTrailers} />
            <InfoField label={t('participants.form.fleetLocomotives', 'Locomotives')} value={participant.fleetLocomotives} />
            <InfoField label={t('participants.form.fleetWagons', 'Wagons')} value={participant.fleetWagons} />
          </div>
        </div>
        <div>
          <div className="text-xs font-medium mb-2">{t('participants.tab.commercial', 'Commercial')}</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <InfoField label={t('participants.form.paymentTermsDays', 'Payment terms (days)')} value={participant.paymentTermsDays} />
            <InfoField
              label={t('participants.form.creditLimit', 'Credit limit')}
              value={participant.creditLimit !== null ? `${participant.creditLimit} ${participant.creditLimitCurrency ?? ''}`.trim() : null}
            />
            <InfoField label={t('participants.form.totalShipmentsCompleted', 'Completed shipments')} value={participant.totalShipmentsCompleted} />
            <InfoField label={t('participants.form.totalVolumeTons', 'Volume handled (t)')} value={participant.totalVolumeTons} />
          </div>
        </div>
      </div>

      {/* Compliance Documents */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-medium">{t('participants.tab.compliance', 'Compliance documents')}</div>
          <Link
            href={`/backend/compliance-documents?participantProfileId=${encodeURIComponent(participant.id)}`}
            className="text-xs text-primary hover:underline"
          >
            {t('participants.tab.actions.viewAllDocs', 'View all')}
          </Link>
        </div>

        {docs.length === 0 ? (
          <div className="text-xs text-muted-foreground">
            {t('participants.tab.noDocs', 'No compliance documents uploaded yet.')}
          </div>
        ) : (
          <div className="rounded-md border overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">{t('compliance.fields.documentType', 'Document Type')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('compliance.fields.documentNumber', 'Document Number')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('compliance.fields.validUntil', 'Valid Until')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('compliance.fields.status', 'Status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {docs.map((doc) => (
                  <tr key={doc.id} className="hover:bg-muted/30">
                    <td className="px-3 py-2">
                      <Link
                        href={`/backend/compliance-documents/${doc.id}`}
                        className="text-primary hover:underline"
                      >
                        {t(`compliance.document_type.${doc.documentType}`, doc.documentType)}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{doc.documentNumber}</td>
                    <td className="px-3 py-2 text-muted-foreground">{doc.validUntil ?? '—'}</td>
                    <td className="px-3 py-2">
                      <Badge variant={statusVariant(doc.status)}>
                        {t(`compliance.status.${doc.status}`, doc.status)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
